// @flow

import React from "react";
import { localize } from "redux-i18n";

import { Button, View } from "@components";
import type { AbsenceMonitoringLevel } from "@modules/service/reducer/absence-monitoring/types";
import MonitoringLevelHeader from "./monitoring-level-header";
import MonitoringLevelDetails from "./monitoring-level-details";
import MonitoringLevelSettings from "./monitoring-level-settings";
import "./monitoring-levels.scss";

type Props = $Exact<{
  levels: Array<AbsenceMonitoringLevel>,
  alerts: Array<Object>,
  onChange: (levels: Array<AbsenceMonitoringLevel>) => void,
  disabled: boolean,
  readonly: boolean,
  validationErrors: ?Array<Object>,
  ...PropsFromLocalize,
}>;

export class MonitoringLevels extends React.Component<Props> {
  static defaultProps = {
    onChange: () => {},
    disabled: false,
    readonly: false,
  };

  static default = {
    id: null,
    name: null,
    description: null,
    alerts: null,
    movements: null,
    enabledTasks: [],
    isEnabled: true,
    reviewPeriodLength: 12,
    autoExpireAfter: 15,
    autoExpireActionLevel: null,
    employeeCount: 0,
  };

  handleNext = (next: Array<AbsenceMonitoringLevel>) => {
    this.props.onChange(next);
  };

  handleAdd = () => {
    const levels = this.props.levels || [];
    const nextId = levels ? levels.length : 0;
    const next = [
      ...(levels || []),
      {
        ...MonitoringLevels.default,
        id: -Math.abs(nextId + 1),
        name: this.props.t("ABSENCE_MONITORING_LEVEL_NO", { no: nextId }),
      },
    ];
    this.handleNext(next);
  };

  handleUpdate = (rowIndex: number) => (level: AbsenceMonitoringLevel) => {
    const next = this.props.levels.map((l: AbsenceMonitoringLevel, index: number) =>
      rowIndex === index ? level : l,
    );
    this.handleNext(next);
  };

  handleDisable = (index: number) => () => {
    const next = this.props.levels.map((l: *, i: number) =>
      i === index ? { ...l, isEnabled: !l.isEnabled } : l,
    );

    this.handleNext(next);
  };

  handleMoveUp = (index: number) => () => {
    const next = this.props.levels.slice(0);
    next.splice(index - 1, 0, next.splice(index, 1)[0]);

    this.handleNext(next);
  };

  handleMoveDown = (index: number) => () => {
    const next = this.props.levels.slice(0);
    next.splice(index + 1, 0, next.splice(index, 1)[0]);

    this.handleNext(next);
  };

  handleDelete = (index: number) => () => {
    let next = this.props.levels.slice(0);
    const [removed] = next.splice(index, 1);
    next = next.map(l => ({
      ...l,
      movements: l.movements ? l.movements.filter(l => l.level.id !== removed.id) : null,
    }));

    this.handleNext(next);
  };

  handleValidateName = (level: AbsenceMonitoringLevel) => (name: string): boolean => {
    const nameAlreadyUsed = !!this.props.levels.find(
      (l: AbsenceMonitoringLevel) => l.name === name && l.id !== level.id,
    );

    if (!name || nameAlreadyUsed) {
      return false;
    }

    return true;
  };

  render() {
    return (
      <View className="content-container!" gap={2}>
        {!this.props.readonly && (
          <View className="monitoring-level__adds" alignItems="end">
            <Button materialIcon="add_box" onClick={this.handleAdd}>
              {this.props.t("ABSENCE_MONITORING_ADD_LEVEL")}
            </Button>
          </View>
        )}
        {this.props.levels.map((level: AbsenceMonitoringLevel, index: number) => (
          <div key={level.id} className="monitoring-level__container content-container p-0">
            <div className="monitoring-level__header">
              <MonitoringLevelHeader
                index={index}
                level={level}
                onDisable={this.handleDisable(index)}
                onMoveUp={index !== 0 ? this.handleMoveUp(index) : undefined}
                onMoveDown={
                  index < this.props.levels.length - 1 ? this.handleMoveDown(index) : undefined
                }
                onDelete={this.props.levels.length > 1 ? this.handleDelete(index) : undefined}
                readonly={this.props.readonly}
              />
            </div>
            <div className="monitoring-level__main">
              <div className="monitoring-level__aside">
                <MonitoringLevelDetails
                  level={level}
                  onChange={this.handleUpdate(index)}
                  disabled={!level.isEnabled}
                  readonly={this.props.readonly}
                  validateName={this.handleValidateName(level)}
                />
              </div>
              <div className="monitoring-level__content">
                <MonitoringLevelSettings
                  level={level}
                  options={{
                    alerts: this.props.alerts,
                    levels: this.props.levels.filter(
                      (l: AbsenceMonitoringLevel) => l.id !== level.id && l.name,
                    ),
                    expiryLevels: this.props.levels.filter(
                      (l: AbsenceMonitoringLevel, i: number) => i < index,
                    ),
                  }}
                  onChange={this.handleUpdate(index)}
                  disabled={!level.isEnabled}
                  readonly={this.props.readonly}
                  validationErrors={(this.props.validationErrors || []).filter(
                    i => i.path === index,
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </View>
    );
  }
}

export default localize()(MonitoringLevels);
